import { useEffect, useState, useMemo, useCallback } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import useAxiosPrivate from "./useAxiosPrivate";
import useUser from "./useUser";
import { useTranslation } from "react-i18next";
import { useUseCases } from "@/context/UseCasesProvider";

const useOrders = (periodFilterInterval, onUpdateLastUpdated, refetchOrders, perPage = 500) => {
  const axiosPrivate = useAxiosPrivate();
  const { data: userData } = useUser();
  const { t, i18n } = useTranslation();
  const [allDataLoaded, setAllDataLoaded] = useState(false);
  const [isAppForeground, setIsAppForeground] = useState(true);

  const useCasesData = useUseCases();
  const settingsEntities = useCasesData?.settings?.entities;
  const hasMultipleEntities = Array.isArray(settingsEntities) && settingsEntities.length > 1;
  const entitiesIdList = userData?.entities || null;

  const godMode = useMemo(() => {
    if (!userData) return undefined; 
    return userData.user_email.includes("@vistaflow.ai");
  }, [userData]);

  const statuses = useMemo(() => {
    if (godMode === undefined) return null;

    const base = ["success", "pending_review", "archive", "failed"];
    if (godMode) base.push("active");
    return base;
  }, [godMode]);

  const isIntervalReady = periodFilterInterval !== null;

  const startDatePeriodInterval = isIntervalReady ? new Date(
    periodFilterInterval.start.year,
    periodFilterInterval.start.month - 1,
    periodFilterInterval.start.day
  ) : new Date();

  const endDatePeriodInterval = isIntervalReady ? new Date(
    periodFilterInterval.end.year,
    periodFilterInterval.end.month - 1,
    periodFilterInterval.end.day
  ) : new Date();

  const formatDateToLocalISO = (date, isEndOfDay = false) => {
    if (isEndOfDay) {
      date.setHours(23, 59, 59, 0);
    } else {
      date.setHours(0, 0, 0, 0);
    }

    const datePart = date.toLocaleDateString("fr-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone: "Europe/Paris",
    });

    const timePart = date.toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZone: "Europe/Paris",
    });

    return `${datePart}T${timePart}`;
  };

  const today = new Date();
  const startIsoDate = formatDateToLocalISO(startDatePeriodInterval);
  const endIsoDate = formatDateToLocalISO(endDatePeriodInterval, true);

  const formatDate = useCallback(
    (date) => {
      const hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, "0");
      return `${date.getDate()} ${date.toLocaleString(i18n.language, {
        month: "long",
      })} ${t("at")} ${hours}h${minutes}`;
    },
    [i18n.language, t]
  );

  const {
    data,
    error,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["orders", statuses, startIsoDate, endIsoDate, entitiesIdList],
    enabled: statuses !== null && isIntervalReady && entitiesIdList !== null,
    queryFn: async ({ pageParam = 1 }) => {
      const statusParam = statuses.join(",");
      let url = `/orders?min_start_datetime=${startIsoDate}&status=${statusParam}&per_page=${perPage}&current_page=${pageParam}`;
      if (hasMultipleEntities) {
        url += `&entities_id_list=${entitiesIdList}`;
      }

      if (
        !(
          endDatePeriodInterval.getFullYear() === today.getFullYear() &&
          endDatePeriodInterval.getMonth() === today.getMonth() &&
          endDatePeriodInterval.getDate() === today.getDate()
        )
      ) {
        url += `&max_start_datetime=${endIsoDate}`;
      }
      const response = await axiosPrivate.get(url);
      return response.data;
    },
    getNextPageParam: (lastPage) => lastPage?.pagination?.next_page || undefined,
    staleTime: 60000,
    gcTime: 60000,
    refetchOnWindowFocus: true,
    refetchInterval: isAppForeground ? 60000 : false,
  });

  const allOrders = useMemo(() => {
    return data?.pages.flatMap((page) => page.data) || [];
  }, [data]);

  useEffect(() => {
    if (isFetching) {
      setAllDataLoaded(false);
    }
  }, [isFetching]);

  useEffect(() => {
    const fetchAllPages = async () => {
      if (hasNextPage && !isFetchingNextPage) {
        await fetchNextPage();
      } else if (!hasNextPage) {
        setAllDataLoaded(true);
      }
    };

    if (!allDataLoaded && !isLoading && hasNextPage) {
      fetchAllPages();
    }

    if (!isLoading && !hasNextPage) {
      const now = new Date();
      onUpdateLastUpdated(formatDate(now));
    }
  }, [
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    allDataLoaded,
    fetchNextPage,
    formatDate,
    onUpdateLastUpdated,
  ]);

  useEffect(() => {
    if (refetchOrders) {
      refetch();
    }
  }, [refetchOrders, refetch]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setIsAppForeground(true);
      } else {
        setIsAppForeground(false);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return {
    allOrders,
    error,
    isLoading,
    isFetchingNextPage,
    allDataLoaded,
  };
};

export default useOrders;
